import React, { useEffect, useRef, useState } from 'react';
/**
 * Badge for the local self-view that confirms the camera video is *physically*
 * being sent out. The authoritative signal is the real PeerConnection
 * outbound-rtp video stats — specifically whether `framesSent` / `bytesSent`
 * are actually increasing between polls. (The aggregated lib-jitsi-meet
 * `framerate` stat proved unreliable: it reported 0 while frames were in fact
 * being transmitted.)
 *
 * - framesSent/bytesSent increasing → 送信中.
 * - flat → 未送信 (no track, muted, or the bridge suspended the layers because
 *   nothing is subscribing to the stream).
 *
 * @returns {JSX.Element}
 */
export default function VideoSendIndicator() {
    const [sending, setSending] = useState(false);
    const [fps, setFps] = useState(0);
    const prev = useRef(null);
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const APPa = typeof APP === 'undefined' ? null : APP;
        if (!APPa) {
            return undefined;
        }
        const tick = async () => {
            try {
                const tracks = APPa.store.getState()['features/base/tracks'];
                const v = tracks.find(t => t.local && t.mediaType === 'video');
                const a = tracks.find(t => t.local && t.mediaType === 'audio');
                const pc = APPa.conference?._room?.getActiveMediaSession?.()?.peerconnection?.peerconnection;
                let frames = 0;
                let bytes = 0;
                let active = false;
                let hasOutbound = false;
                if (pc?.getStats) {
                    const report = await pc.getStats();
                    report.forEach((r) => {
                        if (r.type === 'outbound-rtp' && r.kind === 'video') {
                            hasOutbound = true;
                            frames += r.framesSent || 0;
                            bytes += r.bytesSent || 0;
                            active = active || Boolean(r.active);
                        }
                    });
                }
                const now = typeof performance === 'undefined' ? Date.now() : performance.now();
                let isSending = false;
                let curFps = 0;
                if (prev.current) {
                    const df = frames - prev.current.frames;
                    const db = bytes - prev.current.bytes;
                    const dt = (now - prev.current.ts) / 1000;
                    // Truth = frames/bytes actually left the client in this window.
                    isSending = df > 0 || db > 0;
                    curFps = dt > 0 ? Math.max(0, Math.round(df / dt)) : 0;
                }
                prev.current = { bytes, frames, ts: now };
                setSending(isSending);
                setFps(curFps);
                // eslint-disable-next-line no-console
                console.log('[deepai/video-send]', {
                    sending: isSending,
                    fps: curFps,
                    framesSent: frames,
                    bytesSent: bytes,
                    active,
                    hasOutbound,
                    video: v ? { muted: v.muted, videoType: v.videoType } : 'NONE',
                    audio: a ? { muted: a.muted } : 'NONE'
                });
            }
            catch (e) {
                // eslint-disable-next-line no-console
                console.log('[deepai/video-send] diag error', e);
            }
        };
        const iv = setInterval(tick, 2000);
        tick();
        return () => clearInterval(iv);
    }, []);
    return (React.createElement("div", { style: {
            position: 'absolute',
            left: '6px',
            top: '6px',
            zIndex: 12,
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            padding: '2px 8px',
            borderRadius: '999px',
            background: 'rgba(0,0,0,.55)',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            lineHeight: '15px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
        }, title: `送出 ${fps}fps` },
        React.createElement("span", { style: {
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: sending ? '#22c55e' : '#ef4444',
                boxShadow: sending ? '0 0 6px #22c55e' : 'none'
            } }),
        sending ? `送信中 ${fps}fps` : '未送信'));
}
